/* crm-pipeline-mgmt.jsx — Pipeline Management
   Provides: PipelineManagerView, PipelinePicker
   All components self-register on window. */

const { useState, useEffect, useCallback } = React;

/* ── Constants ─────────────────────────────────────────────────── */
const STAGE_KINDS = [
  { value: 'OPEN', label: 'Open', tone: 'blue',  color: '#6366f1' },
  { value: 'WON',  label: 'Won',  tone: 'green', color: '#10B981' },
  { value: 'LOST', label: 'Lost', tone: 'red',   color: '#EF4444' },
];
const KIND_TONE  = { OPEN: 'blue',  WON: 'green',  LOST: 'red'  };
const KIND_LABEL = { OPEN: 'Open',  WON: 'Won',    LOST: 'Lost' };

const STAGE_COLORS = ['#6366f1','#0EA5E9','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#64748B'];
const BLANK_STAGE  = { name: '', kind: 'OPEN', probability: 50, color: '#6366f1', slaHours: '' };

const deletePipelineDesc = (pipeline) => {
  if ((pipeline.stageCount || 0) > 0) return `This pipeline has ${pipeline.stageCount} stage(s). Remove all stages first.`;
  if (pipeline.isDefault) return 'This is the default pipeline. Another pipeline will be promoted to default automatically.';
  return 'This action cannot be undone.';
};

/* ── StageForm — shared between add and inline edit ─────────────── */
const StageForm = ({ draft, onChange, onSave, onCancel, saving, saveLabel }) => {
  const handleKindChange = (kind) => {
    const kd = STAGE_KINDS.find(k => k.value === kind);
    onChange(d => ({ ...d, kind, color: kd?.color || d.color }));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8 }}>
        <input
          className="nx-input"
          placeholder="Stage name *"
          value={draft.name}
          onChange={e => onChange(d => ({ ...d, name: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') { onSave(); } }}
          autoFocus
          disabled={saving}
        />
        <select
          className="nx-input"
          value={draft.kind}
          onChange={e => handleKindChange(e.target.value)}
          disabled={saving}
        >
          {STAGE_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, alignItems: 'end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--nx-text-muted)' }}>
          <span>Win probability (%)</span>
          <input
            className="nx-input"
            type="number"
            min={0}
            max={100}
            value={draft.probability}
            onChange={e => onChange(d => ({ ...d, probability: e.target.value }))}
            disabled={saving}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--nx-text-muted)' }}>
          <span>SLA hours (optional)</span>
          <input
            className="nx-input"
            type="number"
            min={1}
            value={draft.slaHours}
            onChange={e => onChange(d => ({ ...d, slaHours: e.target.value }))}
            placeholder="e.g. 72"
            disabled={saving}
          />
        </label>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Color:</span>
        <div className="nx-stage-color-picker">
          {STAGE_COLORS.map(c => (
            <button
              key={c}
              type="button"
              className={'nx-stage-color-swatch' + (draft.color === c ? ' is-active' : '')}
              style={{ background: c }}
              onClick={() => onChange(d => ({ ...d, color: c }))}
              disabled={saving}
            />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="primary" size="sm" onClick={onSave} disabled={!draft.name.trim() || saving}>
          {saving ? '…' : saveLabel}
        </Btn>
        <Btn variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Btn>
      </div>
    </div>
  );
};

/* ── StageRow — display + inline edit ──────────────────────────── */
const StageRow = ({ stage, index, total, canManage, onUpdate, onRemove, onMoveUp, onMoveDown, reordering, removing }) => {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState({ name: stage.name, kind: stage.kind || 'OPEN', probability: stage.probability ?? 0, color: stage.color || '#6366f1', slaHours: stage.slaHours || '' });
  const [saving,  setSaving]  = useState(false);

  const handleSave = async () => {
    if (!draft.name.trim() || saving) return;
    setSaving(true);
    try {
      await onUpdate({
        name:        draft.name.trim(),
        tone:        KIND_TONE[draft.kind] || 'blue',
        kind:        draft.kind,
        probability: Number(draft.probability) || 0,
        color:       draft.color,
        slaHours:    draft.slaHours ? Number(draft.slaHours) : undefined,
      });
      setEditing(false);
    } catch {
      /* parent already showed toast — keep form open */
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft({ name: stage.name, kind: stage.kind || 'OPEN', probability: stage.probability ?? 0, color: stage.color || '#6366f1', slaHours: stage.slaHours || '' });
    setEditing(false);
  };

  const disabled   = reordering || removing === stage.id;
  const trashIcon  = removing === stage.id ? 'Loader2' : 'Trash2';

  return (
    <div
      className="nx-stage-editor-row"
      style={{
        flexDirection: editing ? 'column' : 'row',
        alignItems:    editing ? 'stretch' : 'center',
        opacity:       removing === stage.id ? 0.5 : 1,
        transition:    'opacity 0.15s',
      }}
    >
      {editing ? (
        <StageForm
          draft={draft}
          onChange={setDraft}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          saveLabel="Save Changes"
        />
      ) : (
        <>
          <div className="nx-stage-editor-dot" style={{ background: stage.color || '#6366f1', flexShrink: 0 }} />
          <span className="nx-stage-editor-name">{stage.name}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
            <Pill tone={KIND_TONE[stage.kind] || 'blue'}>{KIND_LABEL[stage.kind] || 'Open'}</Pill>
            <span className="nx-muted nx-mini" style={{ fontVariantNumeric: 'tabular-nums' }}>{stage.probability}%</span>
            {stage.slaHours && <span className="nx-muted nx-mini">{stage.slaHours}h SLA</span>}
            {stage.isDefault && <Pill tone="purple">Default</Pill>}
          </div>
          {canManage && (
            <div style={{ display: 'flex', gap: 2, marginLeft: 8, flexShrink: 0 }}>
              <IconBtn icon="ChevronUp"   size={13} onClick={onMoveUp}              disabled={index === 0         || disabled} title="Move up"   />
              <IconBtn icon="ChevronDown" size={13} onClick={onMoveDown}             disabled={index === total - 1 || disabled} title="Move down" />
              <IconBtn icon="Pencil"      size={13} onClick={() => setEditing(true)} disabled={disabled}           title="Edit"      />
              <IconBtn icon={trashIcon}   size={13} onClick={onRemove}              disabled={disabled}           title="Delete stage" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════
   PIPELINE MANAGER VIEW
   ══════════════════════════════════════════════════════════════════ */
const PipelineManagerView = ({ workspaceId }) => {
  const store     = useStore();
  const toast     = useToast();
  const wsId      = workspaceId || window.__nx_auth?.user?.activeWorkspaceId;
  const canManage = !!(window.__nx_rbac?.can('crm.pipeline.manage'));

  /* ── Pipelines ── */
  const [pipelines,   setPipelines]   = useState([]);
  const [plLoading,   setPlLoading]   = useState(true);
  const [plError,     setPlError]     = useState(null);
  const [selPipeline, setSelPipeline] = useState(null);

  /* ── Pipeline create ── */
  const [showCreate,  setShowCreate]  = useState(false);
  const [newPlName,   setNewPlName]   = useState('');
  const [creating,    setCreating]    = useState(false);

  /* ── Pipeline rename ── */
  const [renamingPl,  setRenamingPl]  = useState(null);
  const [renameVal,   setRenameVal]   = useState('');
  const [renaming,    setRenaming]    = useState(false);

  /* ── Pipeline delete ── */
  const [removingPl,  setRemovingPl]  = useState(null);

  /* ── Stages ── */
  const [stages,      setStages]      = useState([]);
  const [stgLoading,  setStgLoading]  = useState(false);
  const [stgError,    setStgError]    = useState(null);

  /* ── Stage add ── */
  const [showAddStg,  setShowAddStg]  = useState(false);
  const [stageDraft,  setStageDraft]  = useState(BLANK_STAGE);
  const [savingStg,   setSavingStg]   = useState(false);

  /* ── Stage remove / reorder ── */
  const [removingStg, setRemovingStg] = useState(null);
  const [reordering,  setReordering]  = useState(false);

  /* ── Load pipelines ── */
  const loadPipelines = useCallback(async () => {
    if (!wsId) return;
    setPlLoading(true);
    setPlError(null);
    try {
      const res  = await window.__nx_api.crmPipelines.list(wsId);
      const data = Array.isArray(res) ? res : (res?.data ?? []);
      setPipelines(data);
      setSelPipeline(prev => {
        if (!prev) return data[0] || null;
        return data.find(p => p.id === prev.id) || data[0] || null;
      });
    } catch (e) {
      setPlError(e?.message || 'Failed to load pipelines');
    } finally {
      setPlLoading(false);
    }
  }, [wsId]);

  useEffect(() => { loadPipelines(); }, [loadPipelines]);

  /* ── Load stages for selected pipeline ── */
  useEffect(() => {
    if (!selPipeline) { setStages([]); return; }
    let cancelled = false;
    setStgLoading(true);
    setStgError(null);
    window.__nx_api.crmPipelines.findOne(wsId, selPipeline.id)
      .then(res => { if (!cancelled) setStages(Array.isArray(res?.stages) ? res.stages : []); })
      .catch(e  => { if (!cancelled) setStgError(e?.message || 'Failed to load stages'); })
      .finally(() => { if (!cancelled) setStgLoading(false); });
    return () => { cancelled = true; };
  }, [selPipeline?.id, wsId]);

  /* ── Pipeline CRUD ── */
  const handleSetDefault = async (pipeline) => {
    try {
      await window.__nx_api.crmPipelines.update(wsId, pipeline.id, { isDefault: true });
      toast({ tone: 'success', icon: 'Star', title: `"${pipeline.name}" is now the default pipeline` });
      await loadPipelines();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to set default pipeline' });
    }
  };

  const handleCreatePipeline = async () => {
    if (!newPlName.trim() || creating) return;
    setCreating(true);
    try {
      await window.__nx_api.crmPipelines.create(wsId, { name: newPlName.trim() });
      setNewPlName('');
      setShowCreate(false);
      toast({ tone: 'success', icon: 'Check', title: 'Pipeline created' });
      await loadPipelines();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to create pipeline' });
    } finally {
      setCreating(false);
    }
  };

  const handleRenamePipeline = async () => {
    if (!renameVal.trim() || renaming || !renamingPl) return;
    setRenaming(true);
    try {
      await window.__nx_api.crmPipelines.update(wsId, renamingPl.id, { name: renameVal.trim() });
      toast({ tone: 'success', icon: 'Check', title: 'Pipeline renamed' });
      setRenamingPl(null);
      await loadPipelines();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to rename pipeline' });
    } finally {
      setRenaming(false);
    }
  };

  const handleDeletePipeline = (pipeline) => {
    store.confirm({
      title:       `Delete "${pipeline.name}"?`,
      description: deletePipelineDesc(pipeline),
      danger:      true,
      onConfirm:   async () => {
        setRemovingPl(pipeline.id);
        try {
          await window.__nx_api.crmPipelines.remove(wsId, pipeline.id);
          toast({ tone: 'success', icon: 'Check', title: 'Pipeline deleted' });
          if (selPipeline?.id === pipeline.id) setSelPipeline(null);
          await loadPipelines();
        } catch (e) {
          toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to delete pipeline' });
        } finally {
          setRemovingPl(null);
        }
      },
    });
  };

  /* ── Stage CRUD ── */
  const handleAddStage = async () => {
    if (!stageDraft.name.trim() || savingStg || !selPipeline) return;
    setSavingStg(true);
    try {
      const created = await window.__nx_api.stages.create(wsId, {
        name:        stageDraft.name.trim(),
        tone:        KIND_TONE[stageDraft.kind] || 'blue',
        kind:        stageDraft.kind,
        probability: Number(stageDraft.probability) || 0,
        color:       stageDraft.color,
        pipelineId:  selPipeline.id,
        ...(stageDraft.slaHours ? { slaHours: Number(stageDraft.slaHours) } : {}),
      });
      setStages(s => [...s, created]);
      setPipelines(p => p.map(pl => pl.id === selPipeline.id ? { ...pl, stageCount: (pl.stageCount || 0) + 1 } : pl));
      setStageDraft(BLANK_STAGE);
      setShowAddStg(false);
      toast({ tone: 'success', icon: 'Check', title: 'Stage added' });
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to add stage' });
    } finally {
      setSavingStg(false);
    }
  };

  const handleUpdateStage = async (stageId, data) => {
    try {
      const updated = await window.__nx_api.stages.update(wsId, stageId, data);
      setStages(s => s.map(x => x.id === stageId ? (updated || x) : x));
      toast({ tone: 'success', icon: 'Check', title: 'Stage updated' });
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to update stage' });
      throw e;
    }
  };

  const handleDeleteStage = (stage) => {
    store.confirm({
      title:       `Delete stage "${stage.name}"?`,
      description: 'All deals in this stage must be moved or archived first. This cannot be undone.',
      danger:      true,
      onConfirm:   async () => {
        setRemovingStg(stage.id);
        try {
          await window.__nx_api.stages.remove(wsId, stage.id);
          setStages(s => s.filter(x => x.id !== stage.id));
          setPipelines(p => p.map(pl => pl.id === selPipeline?.id ? { ...pl, stageCount: Math.max(0, (pl.stageCount || 1) - 1) } : pl));
          toast({ tone: 'success', icon: 'Check', title: 'Stage deleted' });
        } catch (e) {
          toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to delete stage' });
        } finally {
          setRemovingStg(null);
        }
      },
    });
  };

  const handleMoveStage = async (index, direction) => {
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= stages.length || reordering) return;
    setReordering(true);
    const stageA = stages[index];
    const stageB = stages[swapIdx];
    try {
      await window.__nx_api.stages.reorder(wsId, [
        { stageId: stageA.id, order: stageB.order },
        { stageId: stageB.id, order: stageA.order },
      ]);
      setStages(s => {
        const next = [...s];
        [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
        return next;
      });
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to reorder' });
    } finally {
      setReordering(false);
    }
  };

  /* ── Render helpers ── */
  const selectPipeline = (p) => { setSelPipeline(p); setShowAddStg(false); setRenamingPl(null); };

  const openRename = (p) => { setSelPipeline(p); setRenamingPl(p); setRenameVal(p.name); setShowAddStg(false); };

  /* Sidebar pipeline list content */
  let sidebarListContent;
  if (plLoading) {
    sidebarListContent = <><SkeletonRow /><SkeletonRow /></>;
  } else if (plError) {
    sidebarListContent = <div style={{ padding: '8px 6px', fontSize: 12, color: 'var(--nx-danger)' }}>{plError}</div>;
  } else if (pipelines.length === 0) {
    sidebarListContent = (
      <div style={{ padding: '16px 6px', fontSize: 12, color: 'var(--nx-text-muted)', textAlign: 'center', lineHeight: 1.5 }}>
        No pipelines yet.{canManage && <><br />Click <strong>+</strong> to create one.</>}
      </div>
    );
  } else {
    sidebarListContent = pipelines.map(p => {
      const isActive    = selPipeline?.id === p.id;
      const isRemoving  = removingPl === p.id;
      const stageCount  = isActive ? stages.length : (p.stageCount ?? 0);
      const plItemClass = 'nx-pipeline-mgr-item' + (isActive ? ' is-active' : '');
      return (
        <div key={p.id} className={plItemClass} style={{ opacity: isRemoving ? 0.5 : 1 }}>
          <button
            type="button"
            onClick={() => selectPipeline(p)}
            style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', minWidth: 0, textAlign: 'left', color: 'inherit', font: 'inherit' }}
          >
            <span className="nx-pipeline-mgr-item-name">{p.name}</span>
            <span className="nx-muted nx-mini" style={{ flexShrink: 0 }}>{stageCount}</span>
            {p.isDefault && <Pill tone="purple">Default</Pill>}
          </button>
          {canManage && (
            <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {!p.isDefault && (
                <IconBtn
                  icon="Star"
                  size={11}
                  onClick={() => handleSetDefault(p)}
                  disabled={!!renamingPl || isRemoving}
                  title="Set as default pipeline"
                />
              )}
              <IconBtn
                icon="Pencil"
                size={11}
                onClick={() => openRename(p)}
                disabled={!!renamingPl || isRemoving}
                title="Rename pipeline"
              />
              <IconBtn
                icon={isRemoving ? 'Loader2' : 'Trash2'}
                size={11}
                onClick={() => handleDeletePipeline(p)}
                disabled={isRemoving}
                title="Delete pipeline"
              />
            </div>
          )}
        </div>
      );
    });
  }

  /* Stage panel content */
  let stagePanelContent;
  if (stgLoading) {
    stagePanelContent = <><SkeletonRow /><SkeletonRow /><SkeletonRow /></>;
  } else if (stgError) {
    stagePanelContent = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(220,38,38,0.08)', borderRadius: 6 }}>
        <NexusIcon name="AlertCircle" size={14} style={{ color: 'var(--nx-danger)', flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: 'var(--nx-danger)' }}>{stgError}</span>
      </div>
    );
  } else if (stages.length === 0 && !showAddStg) {
    const emptyDesc = canManage
      ? 'Add stages to define the deal progression for this pipeline.'
      : 'No stages have been configured for this pipeline.';
    const emptyAction = canManage && (
      <Btn variant="outline" size="sm" icon="Plus" onClick={() => { setStageDraft(BLANK_STAGE); setShowAddStg(true); }}>
        Add Stage
      </Btn>
    );
    stagePanelContent = <EmptyState icon="Layers" title="No stages yet" desc={emptyDesc} compact action={emptyAction} />;
  } else {
    stagePanelContent = (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {stages.map((s, i) => (
          <StageRow
            key={s.id}
            stage={s}
            index={i}
            total={stages.length}
            canManage={canManage}
            onUpdate={data => handleUpdateStage(s.id, data)}
            onRemove={() => handleDeleteStage(s)}
            onMoveUp={() => handleMoveStage(i, 'up')}
            onMoveDown={() => handleMoveStage(i, 'down')}
            reordering={reordering}
            removing={removingStg}
          />
        ))}
      </div>
    );
  }

  const stagePlural      = stages.length === 1 ? '' : 's';
  const stageCountLabel  = stgLoading ? 'Loading…' : `${stages.length} stage${stagePlural}`;
  const isRenamingActive = renamingPl?.id === selPipeline?.id;

  /* ── Render ── */
  return (
    <div className="nx-pipeline-mgr" style={{ gridTemplateColumns: '240px 1fr', minHeight: 560 }}>

      {/* ─── LEFT SIDEBAR ─── */}
      <div className="nx-pipeline-mgr-sidebar">

        {/* Sidebar header */}
        <div className="nx-pipeline-mgr-sidebar-head">
          <span style={{ flex: 1 }}>Pipelines</span>
          {pipelines.length > 0 && <span className="nx-muted nx-mini">{pipelines.length}</span>}
          {canManage && !showCreate && (
            <IconBtn icon="Plus" size={13} onClick={() => { setShowCreate(true); setNewPlName(''); }} title="New pipeline" />
          )}
        </div>

        {/* New pipeline inline form */}
        {showCreate && (
          <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: 10, marginBottom: 4, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              className="nx-input"
              placeholder="Pipeline name"
              value={newPlName}
              onChange={e => setNewPlName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { handleCreatePipeline(); }
                else if (e.key === 'Escape') { setShowCreate(false); }
              }}
              autoFocus
              disabled={creating}
              style={{ fontSize: 12 }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn variant="primary" size="sm" onClick={handleCreatePipeline} disabled={!newPlName.trim() || creating}>
                {creating ? '…' : 'Create'}
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Btn>
            </div>
          </div>
        )}

        {sidebarListContent}
      </div>

      {/* ─── RIGHT MAIN PANEL ─── */}
      <div className="nx-pipeline-mgr-main">

        {!selPipeline && !plLoading ? (
          <EmptyState
            icon="GitBranch"
            title="Select a pipeline"
            desc={canManage ? 'Choose a pipeline from the left, or create a new one.' : 'Choose a pipeline from the left to view its stages.'}
            action={canManage && !showCreate && (
              <Btn variant="outline" size="sm" icon="Plus" onClick={() => { setShowCreate(true); setNewPlName(''); }}>
                New Pipeline
              </Btn>
            )}
          />
        ) : selPipeline && (
          <>
            {/* Pipeline header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--nx-divider)' }}>
              {isRenamingActive ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                  <input
                    className="nx-input"
                    value={renameVal}
                    onChange={e => setRenameVal(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { handleRenamePipeline(); }
                      else if (e.key === 'Escape') { setRenamingPl(null); }
                    }}
                    autoFocus
                    disabled={renaming}
                    style={{ fontWeight: 700, maxWidth: 260 }}
                  />
                  <Btn variant="primary" size="sm" onClick={handleRenamePipeline} disabled={!renameVal.trim() || renaming}>
                    {renaming ? '…' : 'Save'}
                  </Btn>
                  <Btn variant="ghost" size="sm" onClick={() => setRenamingPl(null)} disabled={renaming}>Cancel</Btn>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="nx-pipeline-mgr-main-title" style={{ fontSize: 15 }}>{selPipeline.name}</span>
                      {selPipeline.isDefault && <Pill tone="purple">Default</Pill>}
                    </div>
                    <div className="nx-muted nx-mini" style={{ marginTop: 2 }}>{stageCountLabel}</div>
                  </div>
                  {canManage && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {!showAddStg && (
                        <Btn
                          variant="ghost"
                          size="sm"
                          icon="Pencil"
                          onClick={() => { setRenamingPl(selPipeline); setRenameVal(selPipeline.name); }}
                          disabled={!!renamingPl}
                        >
                          Rename
                        </Btn>
                      )}
                      {!showAddStg && (
                        <Btn
                          variant="outline"
                          size="sm"
                          icon="Plus"
                          onClick={() => { setStageDraft(BLANK_STAGE); setShowAddStg(true); }}
                        >
                          Add Stage
                        </Btn>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Add stage inline form */}
            {showAddStg && (
              <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>New Stage</div>
                <StageForm
                  draft={stageDraft}
                  onChange={setStageDraft}
                  onSave={handleAddStage}
                  onCancel={() => setShowAddStg(false)}
                  saving={savingStg}
                  saveLabel="Add Stage"
                />
              </div>
            )}

            {stagePanelContent}
          </>
        )}
      </div>
    </div>
  );
};
window.PipelineManagerView = PipelineManagerView;

/* ══════════════════════════════════════════════════════════════════
   PIPELINE PICKER — shown in Deals header when > 1 pipeline
   ══════════════════════════════════════════════════════════════════ */
const PipelinePicker = ({ activePipelineId, pipelines, onChange }) => {
  const [open, setOpen] = useState(false);
  if (!pipelines || pipelines.length <= 1) return null;
  const active = pipelines.find(p => p.id === activePipelineId) || pipelines[0];
  return (
    <span style={{ position: 'relative' }}>
      <Btn variant="secondary" size="sm" icon="GitBranch" onClick={() => setOpen(o => !o)}>
        {active?.name || 'All Pipelines'} <NexusIcon name="ChevronDown" size={10} />
      </Btn>
      {open && (
        <div
          className="nx-chip-menu"
          style={{ top: 'calc(100% + 4px)', minWidth: 180 }}
          onMouseLeave={() => setOpen(false)}
        >
          {pipelines.map(p => (
            <button
              key={p.id}
              type="button"
              className={'nx-chip-menu-item' + (p.id === activePipelineId ? ' is-active' : '')}
              onClick={() => { onChange(p.id); setOpen(false); }}
            >
              <NexusIcon name="Check" size={11} style={{ opacity: p.id === activePipelineId ? 1 : 0, flexShrink: 0 }} />
              <span>{p.name}</span>
              {p.isDefault && <Pill tone="purple">Default</Pill>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
};
window.PipelinePicker = PipelinePicker;
